import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./ZeroTeacherSchoolsModal.css";

const PAGE_SIZE = 8;

const VARIANTS = {
  0: {
    key: "zero",
    title: "Zero Teacher Schools",
    eyebrow: "Teacher deployment exception",
    description:
      "Schools with active student enrolment but no assigned teachers. Use this pop screen to search, filter, verify and initiate deployment action without leaving the dashboard.",
    gapLabel: "Critical staffing gap",
    riskLabel: "Learning continuity risk",
    priorityValue: "72h",
    priorityNote: "Verification target",
    emptyMsg: "No zero-teacher schools found",
    exportFile: "zero_teacher_schools.csv",
    footerAction: "Create deployment action",
  },
  1: {
    key: "one",
    title: "One Teacher Schools",
    eyebrow: "Staffing coverage alert",
    description:
      "Schools operating with only one teacher on record. Search, filter and review PTR-sensitive schools to plan reinforcement without leaving the dashboard.",
    gapLabel: "Coverage vulnerability",
    riskLabel: "Instructional continuity risk",
    priorityValue: "7d",
    priorityNote: "Reinforcement review",
    emptyMsg: "No one-teacher schools found",
    exportFile: "one_teacher_schools.csv",
    footerAction: "Create reinforcement action",
  },
  2: {
    key: "two",
    title: "Two Teacher Schools",
    eyebrow: "Low staffing strength review",
    description:
      "Schools with two teachers mapped. Identify PTR and capacity risks early and initiate block-level monitoring before gaps become critical.",
    gapLabel: "Low staffing strength",
    riskLabel: "Capacity monitoring needed",
    priorityValue: "14d",
    priorityNote: "Monitoring target",
    emptyMsg: "No two-teacher schools found",
    exportFile: "two_teacher_schools.csv",
    footerAction: "Create monitoring action",
  },
};

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);

const ptrFor = (school, teacherCount) => {
  const students = school.student_count || 0;
  const teachers = school.teacher_count ?? teacherCount;
  if (teachers <= 0) return students;
  return Math.round((students / teachers) * 10) / 10;
};

const riskLevel = (school, teacherCount) => {
  const students = school.student_count || 0;
  const ptr = ptrFor(school, teacherCount);

  if (teacherCount === 0) {
    if (students >= 300) return { label: "Very High", className: "very-high" };
    if (students >= 100) return { label: "High", className: "high" };
    return { label: "Medium", className: "medium" };
  }

  if (ptr >= 80) return { label: "Very High", className: "very-high" };
  if (ptr >= 40) return { label: "High", className: "high" };
  return { label: "Medium", className: "medium" };
};

const schoolHint = (school, risk, teacherCount) => {
  const students = school.student_count || 0;
  const ptr = ptrFor(school, teacherCount);

  if (teacherCount === 0) {
    if (students >= 350) return "Large enrolment with no teacher mapped";
    if (students >= 200) return "Prioritize student continuity plan";
    if (students >= 150) return "Deployment mapping missing";
    if (students >= 80) return "Confirm teacher count before closure";
    if (risk.label === "Very High") return "Immediate deployment validation required";
    if (risk.label === "High") return "Verify teacher posting and reporting status";
    return "Block-level staffing anomaly";
  }

  if (teacherCount === 1) {
    if (ptr >= 60) return "Single teacher handling high enrolment — urgent reinforcement";
    if (ptr >= 30) return "Coverage vulnerability — plan second teacher deployment";
    return "Monitor single-teacher coverage and leave contingency";
  }

  if (ptr >= 50) return "Two teachers for large enrolment — PTR still elevated";
  if (ptr >= 25) return "Review timetable and subject coverage adequacy";
  return "Low staffing strength — schedule block review";
};

function downloadCSV(rows, filename, teacherCount) {
  if (!rows?.length) return;
  const headers = ["school_name", "block_name", "udise_code", "student_count", "teacher_count", "ptr", "risk"];
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          if (h === "risk") return JSON.stringify(riskLevel(r, teacherCount).label);
          if (h === "ptr") return JSON.stringify(ptrFor(r, teacherCount));
          return JSON.stringify(r[h] ?? "");
        })
        .join(","),
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

const HeaderIcon = ({ variant }) => {
  if (variant === "one") {
    return (
      <svg className="zts-large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  }
  if (variant === "two") {
    return (
      <svg className="zts-large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  return (
    <svg className="zts-large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
};

const TeacherCountSchoolsModal = ({ open, onClose, data, loading, scopeLabel, teacherCount = 0 }) => {
  const navigate = useNavigate();
  const variant = VARIANTS[teacherCount] ?? VARIANTS[0];
  const [search, setSearch] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [studentsMin, setStudentsMin] = useState("");
  const [studentsMax, setStudentsMax] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open) {
      setSearch("");
      setBlockFilter("");
      setStudentsMin("");
      setStudentsMax("");
      setPage(1);
      window.scrollTo(0, 0);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, teacherCount]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const schools = useMemo(() => (Array.isArray(data?.schools) ? data.schools : []), [data?.schools]);

  const blockOptions = useMemo(() => {
    const blocks = [...new Set(schools.map((s) => s.block_name).filter(Boolean))].sort();
    return blocks;
  }, [schools]);

  const filteredSchools = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = studentsMin === "" ? 0 : parseInt(studentsMin, 10);
    const max = studentsMax === "" ? Infinity : parseInt(studentsMax, 10);

    return schools.filter((school) => {
      const haystack = `${school.school_name || ""} ${school.block_name || ""} ${school.udise_code || ""}`.toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (blockFilter && school.block_name !== blockFilter) return false;
      const students = school.student_count || 0;
      if (!Number.isNaN(min) && students < min) return false;
      if (!Number.isNaN(max) && students > max) return false;
      return true;
    });
  }, [schools, search, blockFilter, studentsMin, studentsMax]);

  useEffect(() => {
    setPage(1);
  }, [search, blockFilter, studentsMin, studentsMax, teacherCount]);

  const totalPages = Math.max(1, Math.ceil(filteredSchools.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageSchools = filteredSchools.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const summary = useMemo(() => {
    const affected = data?.total_schools ?? schools.length;
    const studentsImpacted = schools.reduce((sum, s) => sum + (s.student_count || 0), 0);
    const blocksCovered = new Set(schools.map((s) => s.block_name).filter(Boolean)).size;
    return { affected, studentsImpacted, blocksCovered };
  }, [data, schools]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, safePage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i += 1) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  const clearFilters = () => {
    setSearch("");
    setBlockFilter("");
    setStudentsMin("");
    setStudentsMax("");
  };

  const goToSchool = (udise) => {
    if (!udise) return;
    onClose();
    navigate(`/school/${udise}`);
  };

  const handleExport = () => {
    if (!filteredSchools.length) {
      toast.info("No schools to export");
      return;
    }
    downloadCSV(filteredSchools, variant.exportFile, teacherCount);
    toast.success(`Exported ${filteredSchools.length} schools`);
  };

  const handleCreateAction = () => {
    toast.info("Action workflow — connect to your deployment tracker when ready.");
  };

  if (!open) return null;

  const eyebrow = scopeLabel ? `${variant.eyebrow} · ${scopeLabel}` : `${variant.eyebrow} · District view`;
  const modalId = `tcsModalTitle-${teacherCount}`;

  return createPortal(
    <div
      className="zts-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalId}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <section className={`zts-modal-shell variant-${variant.key}`} onClick={(e) => e.stopPropagation()}>
        <header className="zts-modal-header">
          <div className="zts-modal-title-wrap">
            <div className={`zts-modal-icon ${variant.key}`} aria-hidden="true">
              <HeaderIcon variant={variant.key} />
            </div>
            <div>
              <div className="zts-modal-eyebrow">{eyebrow}</div>
              <h2 id={modalId}>{variant.title}</h2>
              <p>{variant.description}</p>
            </div>
          </div>
          <div className="zts-modal-actions">
            <button type="button" className="zts-ghost-btn" title="Download list" onClick={handleExport}>
              <svg className="zts-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              Export
            </button>
            <button type="button" className="zts-close-x" onClick={onClose} aria-label="Close modal">
              <svg className="zts-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </header>

        <section className="zts-modal-summary" aria-label={`${variant.title} summary`}>
          <article className="zts-summary-mini">
            <small>Affected schools</small>
            <strong>{fmt(summary.affected)}</strong>
            <span>{variant.gapLabel}</span>
          </article>
          <article className="zts-summary-mini warn">
            <small>Students impacted</small>
            <strong>{fmt(summary.studentsImpacted)}</strong>
            <span>{variant.riskLabel}</span>
          </article>
          <article className="zts-summary-mini good">
            <small>Blocks covered</small>
            <strong>{fmt(summary.blocksCovered)}</strong>
            <span>District-wide review</span>
          </article>
          <article className="zts-summary-mini good">
            <small>Priority action</small>
            <strong>{variant.priorityValue}</strong>
            <span>{variant.priorityNote}</span>
          </article>
        </section>

        <section className="zts-modal-body">
          <div className="zts-control-panel" aria-label="Table filters">
            <div className="zts-field">
              <label htmlFor={`tcsSchoolSearch-${teacherCount}`}>Search school / UDISE</label>
              <div className="zts-input-shell">
                <svg className="zts-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  id={`tcsSchoolSearch-${teacherCount}`}
                  placeholder="Type school, block or UDISE..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="zts-field">
              <label htmlFor={`tcsBlockFilter-${teacherCount}`}>Block</label>
              <div className="zts-select-shell">
                <select
                  id={`tcsBlockFilter-${teacherCount}`}
                  value={blockFilter}
                  onChange={(e) => setBlockFilter(e.target.value)}
                >
                  <option value="">All Blocks</option>
                  {blockOptions.map((block) => (
                    <option key={block} value={block}>
                      {block}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="zts-field">
              <label htmlFor={`tcsMinStudents-${teacherCount}`}>Students Min</label>
              <div className="zts-input-shell">
                <input
                  id={`tcsMinStudents-${teacherCount}`}
                  type="number"
                  placeholder="Min"
                  value={studentsMin}
                  onChange={(e) => setStudentsMin(e.target.value)}
                />
              </div>
            </div>
            <div className="zts-field">
              <label htmlFor={`tcsMaxStudents-${teacherCount}`}>Students Max</label>
              <div className="zts-input-shell">
                <input
                  id={`tcsMaxStudents-${teacherCount}`}
                  type="number"
                  placeholder="Max"
                  value={studentsMax}
                  onChange={(e) => setStudentsMax(e.target.value)}
                />
              </div>
            </div>
            <div className="zts-filter-actions">
              <button type="button" className="zts-ghost-btn" onClick={clearFilters}>
                Clear
              </button>
              <button type="button" className="zts-solid-btn" onClick={handleCreateAction}>
                <svg className="zts-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
                Create Action
              </button>
            </div>
          </div>

          <div className="zts-table-area">
            {loading ? (
              <div className="zts-loading-wrap">
                <div className="loading-spinner" />
              </div>
            ) : pageSchools.length > 0 ? (
              <div className="zts-table-scroll">
                <table aria-label={`${variant.title} list`}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>School</th>
                      <th>Block</th>
                      <th>UDISE Code</th>
                      <th>Students</th>
                      <th>Teachers</th>
                      {teacherCount > 0 && <th>PTR</th>}
                      <th>Risk</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageSchools.map((school, index) => {
                      const risk = riskLevel(school, teacherCount);
                      const rank = (safePage - 1) * PAGE_SIZE + index + 1;
                      const count = school.teacher_count ?? teacherCount;
                      return (
                        <tr key={school.udise_code || rank}>
                          <td className="zts-rank">{rank}</td>
                          <td>
                            <div className="zts-school-cell">
                              <button
                                type="button"
                                className="zts-school-link"
                                onClick={() => goToSchool(school.udise_code)}
                              >
                                {school.school_name || "N/A"}
                              </button>
                              <span>{schoolHint(school, risk, teacherCount)}</span>
                            </div>
                          </td>
                          <td>
                            <span className="zts-block-badge">{school.block_name || "N/A"}</span>
                          </td>
                          <td className="zts-mono">{school.udise_code || "N/A"}</td>
                          <td className="zts-students">{fmt(school.student_count)}</td>
                          <td>
                            <span className={`zts-teacher-badge ${variant.key}`}>{count}</span>
                          </td>
                          {teacherCount > 0 && (
                            <td className="zts-mono">{ptrFor(school, teacherCount)} : 1</td>
                          )}
                          <td>
                            <span className={`zts-risk-badge ${risk.className}`}>{risk.label}</span>
                          </td>
                          <td>
                            <button type="button" className="zts-row-action" onClick={() => goToSchool(school.udise_code)}>
                              Review →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="zts-empty-state">
                {schools.length > 0 ? "No schools match the current filters" : variant.emptyMsg}
              </div>
            )}
          </div>
        </section>

        <footer className="zts-modal-footer">
          <div className="zts-footer-left">
            <span>
              Showing {filteredSchools.length ? (safePage - 1) * PAGE_SIZE + 1 : 0}–
              {Math.min(safePage * PAGE_SIZE, filteredSchools.length)} of {fmt(summary.affected)} schools
              {filteredSchools.length !== schools.length && schools.length > 0
                ? ` (${filteredSchools.length} filtered)`
                : ""}
            </span>
            {totalPages > 1 && (
              <div className="zts-pager">
                <button
                  type="button"
                  className="zts-page-btn"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`zts-page-btn${n === safePage ? " active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="zts-page-btn"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
            )}
          </div>
          <div className="zts-footer-actions">
            <button type="button" className="zts-ghost-btn" onClick={onClose}>
              Close
            </button>
            <button type="button" className="zts-solid-btn" onClick={handleCreateAction}>
              <svg className="zts-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 4h14v16H5z" />
                <path d="M9 2h6v4H9z" />
                <path d="M9 11l2 2 4-4" />
              </svg>
              {variant.footerAction}
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body,
  );
};

export default TeacherCountSchoolsModal;
